/* eslint-disable max-lines */
import React, {
    useEffect, useMemo, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Cog, ShieldCheck, Play, Maximize2, Minimize2, AlertCircle, Code
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from 'shared/components/ui/dialog';
import {
    Button
} from 'shared/components/ui/button';
import {
    cn
} from 'shared/lib/utils';
import {
    mcpConnectionService
} from 'shared/api/services/BEX/mcpConnection.service';
import MCPForm from 'builders/builderInternal/components/MCPManager/MCPForm';
import McpTestPanel from '../components/McpTestPanel';
import DSResourceList from './DSResourceList';
import EntityJsonView from '../components/EntityJsonView';

const EMPTY_DRAFT = {
    connection_name: '',
    description: '',
    server_url: '',
    transport: 'streamable_http',
    timeout_ms: 30000,
    auth: {
        type: 'none'
    },
    headers: {}
};

/**
 * The editable FIELDS of a connection, and nothing else.
 *
 * The saved document carries audit and identity keys the form never shows --
 * connection_id, org_id, sub_org_id, created, createdBy, updated, updatedBy.
 * Dirty-tracking used to compare that whole document against the form draft,
 * so the two strings could never match: the badge read "Unsaved" the instant
 * the dialog opened, and still read "Unsaved" after a save that had worked.
 * Anyone watching it concluded their change had not been saved.
 *
 * Projecting BOTH sides through this one function is what makes the comparison
 * mean what it says.
 */
function toDraft(c) {
    return {
        connection_name: c?.connection_name || '',
        description: c?.description || '',
        server_url: c?.server_url || '',
        transport: c?.transport || 'streamable_http',
        timeout_ms: c?.timeout_ms || 30000,
        auth: c?.auth || {
            type: 'none'
        },
        headers: c?.headers || {}
    };
}

/** JSON with object keys sorted, so key ORDER cannot read as a change. */
function stableJson(v) {
    return JSON.stringify(v, (_k, val) => (
        val && typeof val === 'object' && !Array.isArray(val)
            ? Object.keys(val).sort().reduce((acc, kk) => {
                acc[kk] = val[kk];
                return acc;
            }, {})
            : val
    ));
}

/**
 * DSMcpsList — DS-themed list for Library → MCP Servers.
 * Create/Edit open MCPForm in a Dialog inline. No more redirect to legacy
 * `/ai-lab/mcp-builder`. The MCPForm persists internally via mcpConnectionService.
 */
export default function DSMcpsList() {
    const {
        oragID, subOragID
    } = useParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState('create');
    const [formDraft, setFormDraft] = useState(EMPTY_DRAFT);
    const [editingConnection, setEditingConnection] = useState(null);
    const [showTest, setShowTest] = useState(true);
    const [testMaximized, setTestMaximized] = useState(false);
    const [showJson, setShowJson] = useState(false);
    // Reset maximize when dialog closes; open with test pane visible (like ApiForm).
    useEffect(() => {
        if (!formOpen) {
            setTestMaximized(false);
        } else {
            setShowTest(true);
        }
    }, [formOpen]);
    // Dirty tracking — predict/invoke runs against the SAVED MCP doc
    // (server-side auth + headers resolution), so warn when the in-memory
    // draft has diverged from `editingConnection`.
    const initialSnapshot = useMemo(
        () => stableJson(toDraft(editingConnection)),
        [editingConnection]
    );
    const currentSnapshot = stableJson(toDraft(formDraft));
    const pristine = currentSnapshot === initialSnapshot;
    const isEdit = formMode === 'edit';
    const mcpForTest = editingConnection || {
        ...formDraft,
        connection_id: editingConnection?.connection_id || null
    };

    function reload() {
        if (!oragID || !subOragID) return;
        setLoading(true);
        setError(null);
        mcpConnectionService.listMCPConnections(oragID, subOragID)
            .then((arr) => setItems(Array.isArray(arr) ? arr : []))
            .catch((err) => setError(err?.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }
    useEffect(reload, [oragID, subOragID]);

    function openNew() {
        setEditingConnection(null);
        setFormDraft({
            ...EMPTY_DRAFT
        });
        setFormMode('create');
        setFormOpen(true);
    }
    function openEdit(c) {
        setEditingConnection(c);
        setFormDraft(toDraft(c));
        setFormMode('edit');
        setFormOpen(true);
    }
    function handleFormSaved(savedConn) {
        reload();
        if (savedConn?.connection_id) {
            setEditingConnection(savedConn);
            setFormMode('edit');
            setFormDraft(toDraft(savedConn));
            setShowTest(true);
        } else {
            setFormOpen(false);
        }
    }
    async function handleDelete(c) {
        if (!window.confirm(`Delete MCP connection "${c.connection_name || c.connection_id}"?`)) return;
        try {
            await mcpConnectionService.deleteMCPConnection(oragID, subOragID, c.connection_id);
            reload();
        } catch (err) {
            alert('Delete failed: ' + (err?.message || String(err)));
        }
    }

    return (
        <>
            <DSResourceList
                items={items}
                loading={loading}
                error={error}
                getKey={(it) => it.connection_id}
                getCard={(it) => ({
                    icon: Cog,
                    tone: 'violet',
                    title: it.connection_name || it.connection_id,
                    subtitle: it.server_url,
                    description: it.description || '',
                    version: it.version ? `v${it.version}` : null,
                    badge: { tone: 'neutral', label: it.auth?.type || 'none' },
                    stats: [
                        it.transport && {
                            icon: ShieldCheck,
                            label: it.transport
                        }
                    ].filter(Boolean),
                    idLine: it.connection_id,
                    searchTerms: [it.server_url, it.connection_name, it.description]
                })}
                onPick={openEdit}
                onEdit={openEdit}
                onDelete={handleDelete}
                onCreate={openNew}
                newLabel="New MCP server"
                searchPlaceholder="Search MCP servers…"
                emptyTitle="No MCP servers yet"
                emptyDescription="Connect your first MCP server to expose its tools to your assistants."
                emptyIcon={Cog}
            />
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent
                    className={cn(
                        'flex flex-col gap-0 overflow-hidden p-0',
                        showTest
                            ? 'h-[88vh] max-h-[88vh] w-[95vw] max-w-[1400px]'
                            : 'max-h-[88vh] max-w-3xl'
                    )}
                >
                    <DialogHeader className="px-6 pt-5 pb-3 border-b flex-shrink-0">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0 flex-1">
                                <DialogTitle className="flex flex-wrap items-center gap-2">
                                    {isEdit ? 'Edit MCP connection' : 'New MCP connection'}
                                    {showTest && !pristine && (
                                        <span className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
                                            Unsaved
                                        </span>
                                    )}
                                </DialogTitle>
                                <DialogDescription>Saved into <b>Library → MCP Servers</b> for this sub-org.</DialogDescription>
                            </div>
                            <div className="flex flex-shrink-0 items-center gap-1.5">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowJson(true)}
                                    title="View JSON"
                                    className="h-8"
                                >
                                    <Code className="mr-1 h-3 w-3" />
                                    JSON
                                </Button>
                                <Button
                                    type="button"
                                    variant={showTest ? 'secondary' : 'outline'}
                                    size="sm"
                                    onClick={() => setShowTest((v) => !v)}
                                    title={showTest ? 'Hide test panel' : 'Open test panel'}
                                    className="h-8"
                                >
                                    <Play className="mr-1 h-3 w-3" />
                                    {showTest ? 'Hide test' : 'Test'}
                                </Button>
                                {showTest && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setTestMaximized((v) => !v)}
                                        title={testMaximized ? 'Restore split view' : 'Maximize test panel'}
                                        className="h-8 px-2"
                                    >
                                        {testMaximized
                                            ? <Minimize2 className="h-4 w-4" />
                                            : <Maximize2 className="h-4 w-4" />}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </DialogHeader>
                    <div
                        className={cn(
                            'flex min-h-0 flex-1 overflow-hidden',
                            showTest && 'flex-row'
                        )}
                    >
                        <div
                            className={cn(
                                'min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4',
                                showTest && !testMaximized && 'min-w-0 max-w-[50%] border-r',
                                showTest && testMaximized && 'hidden'
                            )}
                        >
                            {formOpen && (
                                <MCPForm
                                    mode={formMode}
                                    orgId={oragID}
                                    subOrgId={subOragID}
                                    connectionId={editingConnection?.connection_id || null}
                                    existing={editingConnection || null}
                                    draft={formDraft}
                                    onDraftChange={setFormDraft}
                                    onSaved={handleFormSaved}
                                    onCancel={() => setFormOpen(false)}
                                />
                            )}
                        </div>
                        {showTest ? (
                            <div
                                className={cn(
                                    'flex min-h-0 flex-1 flex-col overflow-hidden',
                                    !testMaximized && 'min-w-0'
                                )}
                            >
                                {!pristine ? (
                                    <div className="flex-shrink-0 border-b border-amber-300/50 bg-amber-50/60 px-3 py-1.5 text-[11px] text-amber-900">
                                        <AlertCircle className="mr-1 inline h-3 w-3" />
                                        Unsaved edits — discover/invoke runs against the saved version. Save first to test the latest.
                                    </div>
                                ) : null}
                                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                                    <McpTestPanel mcp={mcpForTest} maximized={testMaximized} />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </DialogContent>
            </Dialog>
            <EntityJsonView
                open={showJson}
                onOpenChange={setShowJson}
                title="MCP connection JSON"
                entity={formDraft}
                onApply={(parsed) => {
                    if (!parsed || typeof parsed !== 'object') return;
                    setFormDraft(parsed);
                }}
            />
        </>
    );
}
